import { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Ruler, Weight, Plus, X, Check, Pencil, Trash2, Box } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";

// Controlled vocabularies ONLY — mirrors the backend (no free-form field names).
const TYPES = ["length", "width", "height", "depth", "diameter", "bore", "thickness", "thread", "weight"];
const QUICK = ["length", "width", "height", "diameter", "weight"];   // one-tap chips
const LENGTH_UNITS = ["in", "ft", "cm", "mm"];
const WEIGHT_UNITS = ["lbs", "kg", "oz", "g"];
const isWeight = (t) => t === "weight";
const unitsFor = (t) => (isWeight(t) ? WEIGHT_UNITS : LENGTH_UNITS);
const TYPE_LABEL = (t) => t.charAt(0).toUpperCase() + t.slice(1);
const BOX_SEQ = ["length", "width", "height"];

export const MeasurementsEditor = ({ partId, measurements = [], onChanged, readOnly = false }) => {
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [type, setType] = useState("length");
  const [value, setValue] = useState("");
  const [note, setNote] = useState("");
  const [lenUnit, setLenUnit] = useState("in");     // sticky per family
  const [wtUnit, setWtUnit] = useState("lbs");
  const [boxMode, setBoxMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const valueRef = useRef(null);

  const unit = isWeight(type) ? wtUnit : lenUnit;
  const setUnit = (u) => (isWeight(type) ? setWtUnit(u) : setLenUnit(u));

  const list = [...measurements].sort((a, b) => (a.dimension_class === "weight" ? -1 : b.dimension_class === "weight" ? 1 : 0));

  useEffect(() => { if (open && valueRef.current) valueRef.current.focus(); }, [open, type]);

  const reset = () => { setEditId(null); setType("length"); setValue(""); setNote(""); setBoxMode(false); };

  const startAdd = () => { reset(); setOpen(true); };
  const startBox = () => { reset(); setType("length"); setBoxMode(true); setOpen(true); };
  const startEdit = (m) => { setEditId(m.id); setType(m.type); setValue(String(m.value)); setNote(m.note || ""); (isWeight(m.type) ? setWtUnit : setLenUnit)(m.unit); setBoxMode(false); setOpen(true); };

  const save = async () => {
    if (value === "" || isNaN(Number(value))) { toast.error("Enter a numeric value."); valueRef.current?.focus(); return; }
    setSaving(true);
    try {
      const body = { type, value: Number(value), unit, note: note.trim() };
      const { data } = editId
        ? await api.put(`/parts/${partId}/measurements/${editId}`, body)
        : await api.post(`/parts/${partId}/measurements`, body);
      onChanged?.(data);
      toast.success(`${TYPE_LABEL(type)} ${Number(value)}${unit} saved ✓`);
      if (editId) { reset(); setOpen(false); return; }
      // multi-add: stay open, clear value, advance in box mode, refocus
      setValue(""); setNote("");
      if (boxMode) {
        const nextIdx = BOX_SEQ.indexOf(type) + 1;
        if (nextIdx > 0 && nextIdx < BOX_SEQ.length) setType(BOX_SEQ[nextIdx]);
        else { setBoxMode(false); setType("length"); }
      }
      setTimeout(() => valueRef.current?.focus(), 30);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't save measurement.");
    }
    setSaving(false);
  };

  const remove = async (m) => {
    try {
      const { data } = await api.delete(`/parts/${partId}/measurements/${m.id}`);
      onChanged?.(data);
      toast.success("Measurement removed");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't remove."); }
  };

  return (
    <div data-testid="measurements-editor">
      {/* Specs list */}
      {list.length > 0 && (
        <div data-testid="measurements-list" className="rounded-xl border border-slate-200 divide-y divide-slate-100 mb-2 overflow-hidden">
          {list.map((m) => {
            const wt = m.dimension_class === "weight";
            return (
              <div key={m.id} data-testid={`measurement-${m.type}`} className={`flex items-center gap-2 px-3 py-2 ${wt ? "bg-amber-50" : "bg-white"}`}>
                {wt ? <Weight className="w-4 h-4 text-amber-600 shrink-0" /> : <Ruler className="w-4 h-4 text-slate-400 shrink-0" />}
                <span className={`text-xs font-semibold uppercase tracking-wider ${wt ? "text-amber-700" : "text-slate-500"} w-24 shrink-0`}>{TYPE_LABEL(m.type)}</span>
                <span className="font-mono font-bold text-slate-800 tabular-nums">{m.value}<span className="text-slate-400 ml-0.5 text-xs">{m.unit}</span></span>
                {m.note && <span className="text-[11px] text-slate-400 truncate">· {m.note}</span>}
                {!readOnly && (
                  <div className="ml-auto flex items-center gap-1 shrink-0">
                    <button type="button" data-testid={`measurement-edit-${m.id}`} onClick={() => startEdit(m)} className="p-1 text-slate-400 hover:text-slate-700"><Pencil className="w-3.5 h-3.5" /></button>
                    <button type="button" data-testid={`measurement-remove-${m.id}`} onClick={() => remove(m)} className="p-1 text-slate-400 hover:text-red-600"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {!readOnly && !open && (
        <div className="flex gap-2">
          <Button type="button" size="sm" variant="outline" data-testid="add-measurement-btn" onClick={startAdd} className="rounded-lg h-8 text-xs border-slate-200"><Plus className="w-3.5 h-3.5 mr-1" />Add measurement</Button>
          <Button type="button" size="sm" variant="outline" data-testid="add-box-lwh-btn" onClick={startBox} className="rounded-lg h-8 text-xs border-slate-200 text-slate-500"><Box className="w-3.5 h-3.5 mr-1" />Box (L×W×H)</Button>
        </div>
      )}

      {/* Pull-up entry */}
      {!readOnly && open && (
        <div data-testid="measurement-form" className="rounded-xl border border-[#E0781A]/40 bg-orange-50/40 p-3 space-y-2.5">
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{editId ? "Edit measurement" : boxMode ? `Box — enter ${TYPE_LABEL(type)}` : "New measurement"}</span>
            <button type="button" data-testid="measurement-close" onClick={() => { reset(); setOpen(false); }} className="text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>
          </div>
          {/* type chips */}
          <div className="flex flex-wrap gap-1.5">
            {QUICK.map((t) => (
              <button key={t} type="button" data-testid={`measurement-type-${t}`} onClick={() => setType(t)}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border transition-colors ${type === t ? "bg-slate-800 text-white border-slate-800" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`}>{TYPE_LABEL(t)}</button>
            ))}
            <Select value={QUICK.includes(type) ? "" : type} onValueChange={setType}>
              <SelectTrigger data-testid="measurement-type-more" className="h-[26px] w-[92px] rounded-full text-[11px] px-2.5 border-slate-200"><SelectValue placeholder="More…" /></SelectTrigger>
              <SelectContent>{TYPES.filter((t) => !QUICK.includes(t)).map((t) => <SelectItem key={t} value={t} className="text-xs">{TYPE_LABEL(t)}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Input ref={valueRef} data-testid="measurement-value" type="number" inputMode="decimal" placeholder="Value" value={value}
              onChange={(e) => setValue(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } }}
              className="rounded-lg h-9 text-sm flex-1 font-mono" />
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger data-testid="measurement-unit" className="rounded-lg h-9 text-xs w-24"><SelectValue /></SelectTrigger>
              <SelectContent>{unitsFor(type).map((u) => <SelectItem key={u} value={u} className="text-xs">{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Input data-testid="measurement-note" placeholder="Qualifier (optional) — e.g. at widest point" value={note} onChange={(e) => setNote(e.target.value)} maxLength={120} className="rounded-lg h-8 text-xs" />
          <div className="flex items-center gap-2">
            <Button type="button" size="sm" data-testid="measurement-save" disabled={saving} onClick={save} className="rounded-lg h-8 text-xs bg-[#E0781A] hover:bg-[#C76714] text-white"><Check className="w-3.5 h-3.5 mr-1" />{editId ? "Save" : boxMode ? `Add ${TYPE_LABEL(type)} & next` : "Add"}</Button>
            {!editId && <span className="text-[11px] text-slate-400">Stays open — add several in a row. Enter to save.</span>}
          </div>
        </div>
      )}
    </div>
  );
};
